import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AuroraHeader, AuroraFooter } from "../components/aurora/Header";
import { ExposureGauge } from "../components/aurora/ExposureGauge";
import { ShareButton } from "../components/aurora/ShareButton";
import { ForwardSocialMenu } from "../components/aurora/ForwardSocialMenu";
import { BriefingCTA } from "../components/aurora/BriefingCTA";
import { api } from "../lib/api";
import { tierColor } from "../lib/countries";
import { Lock, RotateCw } from "lucide-react";

const RISK_CATEGORIES = [
  "Trade & Tariff Risk",
  "Supply Chain Vulnerability",
  "Energy & Resource Security",
  "Regulatory & Compliance Risk",
  "Political Stability Risk",
  "Strategic Positioning Risk",
];

export default function PartialResult() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [captured, setCaptured] = useState(false);
  // Post-partial follow-up state: null = not yet answered, 'yes' | 'no'
  const [wantsFullReport, setWantsFullReport] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let pollTimer = null;

    const fetchOnce = async () => {
      try {
        const r = await api.getPartial(id);
        if (cancelled) return;
        setData(r.data);
        setCaptured(r.data.emailCaptured);
        // Seed the confirmable email input from whatever was captured at intake
        setEmail((prev) => prev || r.data.clientEmail || "");
        if (r.data.ready) {
          setLoading(false);
        } else if (r.data.status === "Generation Failed") {
          setLoading(false);
          toast.error("Assessment generation failed. Please try again or contact Aurora.");
        } else {
          // Keep polling every 2s until ready (or component unmounts)
          pollTimer = setTimeout(fetchOnce, 2000);
        }
      } catch (err) {
        if (cancelled) return;
        toast.error("Submission not found");
        setLoading(false);
      }
    };

    fetchOnce();
    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [id]);

  const submitEmail = async (e) => {
    e.preventDefault();
    const trimmed = (email || "").trim();
    if (!trimmed || !/^\S+@\S+\.\S+$/.test(trimmed)) {
      toast.error("Please enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      await api.captureEmail(id, trimmed);
      setCaptured(true);
      toast.success("Confirmed — your full report is on its way");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <AuroraHeader />
        <main className="flex-1 flex items-center justify-center px-6 py-20">
          <div className="text-center max-w-md" data-testid="partial-loading">
            <div className="label-eyebrow text-[#6B4D2B] mb-4">Aurora is preparing your assessment</div>
            <div className="font-heading-bold text-3xl text-[#190F08] mb-6">
              Analysing your operational profile…
            </div>
            <div className="flex justify-center mb-6">
              <div className="w-12 h-12 border-2 border-[#A88059]/30 border-t-[#A88059] rounded-full animate-spin" />
            </div>
            <p className="font-body text-[#6B5E4A] leading-relaxed">
              This typically takes 20&ndash;45&nbsp;seconds &mdash; please keep this tab open.
            </p>
          </div>
        </main>
        <AuroraFooter />
      </div>
    );
  }

  if (!data || !data.partialResult) {
    return (
      <div className="min-h-screen flex flex-col">
        <AuroraHeader />
        <div className="flex-1 flex items-center justify-center font-body text-[#6B5E4A]">
          Assessment not found.
        </div>
        <AuroraFooter />
      </div>
    );
  }

  const { overallScore, tier, tierRationale, executiveSummary } = data.partialResult;

  return (
    <div className="min-h-screen flex flex-col">
      <AuroraHeader />
      <main className="flex-1 max-w-5xl w-full mx-auto px-6 sm:px-10 py-12 sm:py-16">
        <div className="label-eyebrow text-[#6B4D2B] mb-3">Confidential Assessment</div>
        <h2 className="font-heading-bold text-4xl sm:text-5xl text-[#190F08] mb-3" data-testid="company-name">
          {data.companyName || "Your Company"}
        </h2>
        <div className="font-body italic text-[#6B5E4A] mb-10">
          Your Geopolitical Exposure Assessment
        </div>
        {/* Score + Rationale */}
        <div className="grid md:grid-cols-[auto_1fr] gap-12 items-center mb-12">
          <div className="flex justify-center md:justify-start">
            <ExposureGauge score={overallScore} tier={tier} />
          </div>
          <div>
            <div className="label-eyebrow text-[#6B5E4A] mb-3">Overall Exposure Score</div>
            <p
              className="font-heading text-xl text-[#190F08] leading-relaxed mb-4"
              data-testid="tier-rationale"
            >
              {tierRationale}
            </p>
            <div
              className="inline-block px-4 py-1 border font-heading text-xs tracking-[0.22em] uppercase"
              style={{ borderColor: tierColor(tier), color: tierColor(tier) }}
              data-testid="tier-badge"
            >
              {tier} Exposure
            </div>
          </div>
        </div>
        {/* Executive Summary */}
        <section className="mb-16" data-testid="executive-summary">
          <h3 className="font-heading text-3xl text-[#A88059] mb-6">Executive Summary</h3>
          <p className="font-body text-lg text-[#190F08] leading-relaxed mb-5">
            {executiveSummary?.para1}
          </p>
          <p className="font-body text-lg text-[#190F08] leading-relaxed">
            {executiveSummary?.para2}
          </p>
        </section>
        {/* Locked preview */}
        <section className="relative mb-16" data-testid="locked-preview">
          <div className="label-eyebrow text-[#6B5E4A] mb-6">Full Assessment Preview</div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 locked-blur">
            {RISK_CATEGORIES.map((c) => (
              <div key={c} className="border border-[#A88059]/40 p-6">
                <div className="h-2 w-12 bg-[#A88059] mb-4" />
                <div className="font-heading text-lg text-[#190F08] mb-3">{c}</div>
                <div className="font-body text-sm text-[#6B5E4A]">
                  Detailed score, narrative, and recommendations included in your full assessment.
                </div>
              </div>
            ))}
          </div>

          <div className="absolute inset-0 flex items-center justify-center bg-[#190F08]/20">
            <div className="bg-[#FAF5EE] border-2 border-[#A88059] p-8 max-w-md text-center shadow-xl">
              <Lock size={28} className="mx-auto text-[#A88059] mb-4" />
              <div className="font-heading text-xl text-[#190F08] mb-3">
                Full Assessment Locked
              </div>
              <p className="font-body text-[#6B5E4A] text-sm leading-relaxed">
                Your full assessment includes detailed scores, analysis, and recommendations
                across six risk categories.
              </p>
            </div>
          </div>
        </section>
        {/* Follow-up: would you like the full report? */}
        {captured ? (
          <section className="border border-[#A88059] p-10 text-center" data-testid="confirmation-box">
            <div className="label-eyebrow text-[#6B4D2B] mb-3">Confirmed</div>
            <h3 className="font-heading text-3xl text-[#190F08] mb-4">Thank you.</h3>
            <p className="font-body text-[#190F08] text-lg leading-relaxed max-w-xl mx-auto">
              Your full assessment will be personally evaluated and finalised by a senior Aurora
              Insights analyst before delivery to{" "}
              <span className="text-[#6B4D2B]">{email || data.clientEmail}</span>.
            </p>
          </section>
        ) : wantsFullReport === "no" ? (
          <section
            className="border border-[#A88059] p-10 text-center"
            data-testid="declined-box"
          >
            <div className="label-eyebrow text-[#6B4D2B] mb-3">Understood</div>
            <h3 className="font-heading text-3xl text-[#190F08] mb-4">Thank you.</h3>
            <p className="font-body text-[#190F08] text-lg leading-relaxed max-w-xl mx-auto">
              We appreciate you taking the time to complete the assessment. Should you change your
              mind, you can reach us anytime at <a className="text-[#6B4D2B] underline" href="mailto:info@aurorainsights.co">info@aurorainsights.co</a>.
            </p>
          </section>
        ) : wantsFullReport === "yes" ? (
          <section className="border border-[#A88059] p-8 sm:p-10" data-testid="email-confirm">
            <div className="label-eyebrow text-[#6B4D2B] mb-3">Confirm your email</div>
            <h3 className="font-heading text-3xl text-[#190F08] mb-2">
              Where should we send your full report?
            </h3>
            <p className="font-body text-[#6B5E4A] mb-6 text-sm">
              We&rsquo;ll deliver your full assessment to the address below. You can edit it if you
              need to correct a typo.
            </p>
            <form onSubmit={submitEmail} className="flex flex-col sm:flex-row gap-4 items-stretch">
              <input
                className="aurora-input flex-1"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                data-testid="input-confirm-email"
              />
              <button
                type="submit"
                className="btn-primary whitespace-nowrap"
                disabled={submitting}
                data-testid="btn-confirm-email"
              >
                {submitting ? "Confirming..." : "Confirm"}
              </button>
            </form>
            <button
              type="button"
              onClick={() => setWantsFullReport(null)}
              className="btn-ghost mt-6"
              data-testid="btn-back-to-choice"
            >
              ← Back
            </button>
          </section>
        ) : (
          <section className="border border-[#A88059] p-10" data-testid="full-report-prompt">
            <div className="label-eyebrow text-[#6B4D2B] mb-3">Next Step</div>
            <h3 className="font-heading text-3xl text-[#190F08] mb-3">
              Would you like to receive the full report?
            </h3>
            <p className="font-body text-[#6B5E4A] mb-6 text-sm max-w-2xl">
              Your full assessment includes detailed scores, narratives and recommendations across
              all six risk categories, reviewed and finalised by a senior Aurora analyst.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="button"
                onClick={() => setWantsFullReport("yes")}
                className="btn-primary"
                data-testid="btn-want-yes"
              >
                Yes, send me the full report
              </button>
              <button
                type="button"
                onClick={() => setWantsFullReport("no")}
                className="btn-ghost"
                data-testid="btn-want-no"
              >
                No, thanks
              </button>
            </div>
          </section>
        )}

        {/* Action buttons */}
        <div
          className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center justify-between mt-10 pt-8 border-t border-[#A88059]/40"
          data-testid="partial-actions"
        >
          <button
            type="button"
            onClick={() => navigate("/")}
            className="btn-ghost inline-flex items-center justify-center"
            data-testid="btn-retake"
          >
            <RotateCw size={14} className="mr-2" />
            Retake Intake Assessment
          </button>
          <ForwardSocialMenu
            testId="forward-result"
            title="Aurora Insights — My Geopolitical Exposure Assessment"
            text="Aurora Insights has prepared my Geopolitical Exposure Assessment. View the summary here:"
          />
        </div>

        <BriefingCTA testId="partial-briefing-cta" subject={`Schedule a briefing — ${data.companyName || "Aurora Insights"}`} />
      </main>
      <AuroraFooter />
    </div>
  );
}
